/**
 * The record panel: finixt-ui's PanelLayout docked on every page, showing
 * the record the URL names. The strip is neutral, since finixt-ui reserves
 * the accent strip for the assistant and this panel holds records.
 *
 * The body is one dispatcher by kind. A kind without a body yet draws its
 * kind and id, so every opener works from the day the shell does.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';

import { Button } from '../../ui/Button.js';
import { useConfirm } from '../../ui/Confirm.js';
import { ErrorState, IconButton, PanelHeader, PanelLayout } from '../../ui/finixt.js';
import { SetSecretValueForm } from '../forms/SetSecretValueForm.js';
import { AccountRecord } from '../../features/accounts/AccountRecord.js';
import { NewAgentForm } from '../../features/agents/NewAgentForm.js';
import { CreatePartnerForm } from '../../features/federation/CreatePartnerForm.js';
import { InviteStaffForm } from '../../features/platform-staff/InviteStaffForm.js';
import { NewSecretFieldForm } from '../../features/secret-fields/NewSecretFieldForm.js';
import { IssueTokenForm } from '../../features/service-tokens/IssueTokenForm.js';
import { AgentRecord } from '../../features/agents/AgentRecord.js';
import { CollectionRecord } from '../../features/collections/CollectionRecord.js';
import { GroupRecord } from '../../features/collections/GroupRecord.js';
import { PartnerRecord } from '../../features/federation/PartnerRecord.js';
import { OrganizationRecord } from '../../features/organizations/OrganizationRecord.js';
import { StaffRecord } from '../../features/platform-staff/StaffRecord.js';
import { PluginRecord } from '../../features/plugins/PluginRecord.js';
import { ServiceTokenRecord } from '../../features/service-tokens/ServiceTokenRecord.js';
import { TaskRecord } from '../../features/tasks/TaskRecord.js';
import { OrgLinkageRecord } from '../../features/automation/OrgLinkageRecord.js';
import { OrgUserRecord } from '../../features/org-users/OrgUserRecord.js';
import { LinkageRecord } from '../../features/linkages/LinkageRecord.js';
import { DISCARD_FORM, PanelFormGuardContext } from './PanelForm.js';
import {
  type FormName,
  type FormRef,
  type RecordKind,
  type RecordRef,
  useRecordPanel,
} from './record.js';

const KIND_LABEL: Record<RecordKind, string> = {
  organization: 'Organization',
  account: 'Account',
  agent: 'Agent',
  task: 'Task',
  staff: 'Staff',
  group: 'Group',
  collection: 'Collection',
  'service-token': 'Service token',
  partner: 'Partner',
  plugin: 'Plugin',
  linkage: 'Linkage',
  'org-linkage': 'Linkage',
  'org-user': 'User',
};

export function kindLabel(kind: RecordKind): string {
  return KIND_LABEL[kind];
}

const FORM_TITLE: Record<FormName, string> = {
  'invite-staff': 'Invite a staff member',
  'issue-token': 'Issue a service token',
  'new-secret-field': 'New secret field',
  'create-partner': 'Create a federated partner',
  'set-secret': 'Set a secret value',
  'new-agent': 'New agent',
};

export function formTitle(name: FormName): string {
  return FORM_TITLE[name];
}

/** The dispatcher for forms: one body per name. */
export function FormBody({ form }: { form: FormRef }): ReactNode {
  switch (form.name) {
    case 'invite-staff':
      return <InviteStaffForm />;
    case 'issue-token':
      return <IssueTokenForm />;
    case 'new-secret-field':
      return <NewSecretFieldForm />;
    case 'create-partner':
      return <CreatePartnerForm />;
    case 'set-secret':
      return <SetSecretValueForm id={form.id} />;
    case 'new-agent':
      return <NewAgentForm orgId={form.orgId ?? ''} />;
  }
}

/** A record the viewer cannot read, or one that does not exist. */
export function RecordNotFound({ record }: { record: RecordRef }) {
  return (
    <ErrorState
      kind="notFound"
      compact
      title={`No ${kindLabel(record.kind).toLowerCase()} here`}
      body="It may have been removed, or you may not have access to it."
      code={record.id}
    />
  );
}

/** The dispatcher: one body per kind. */
export function RecordBody({ record }: { record: RecordRef }): ReactNode {
  switch (record.kind) {
    case 'organization':
      return <OrganizationRecord record={record} />;
    case 'account':
      return <AccountRecord record={record} />;
    case 'task':
      return <TaskRecord record={record} />;
    case 'staff':
      return <StaffRecord record={record} />;
    case 'partner':
      return <PartnerRecord record={record} />;
    case 'plugin':
      return <PluginRecord record={record} />;
    case 'service-token':
      return <ServiceTokenRecord record={record} />;
    case 'agent':
      return <AgentRecord record={record} />;
    case 'group':
      return <GroupRecord record={record} />;
    case 'collection':
      return <CollectionRecord record={record} />;
    case 'linkage':
      return <LinkageRecord record={record} />;
    case 'org-linkage':
      return <OrgLinkageRecord record={record} />;
    case 'org-user':
      return <OrgUserRecord record={record} />;
  }
}

/**
 * Closes on Escape and gives focus back to whatever opened the record, the
 * way the dialogs do. Only the shell renders this, once.
 */
function useRecordPanelHost(open: boolean, close: () => void) {
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const stopWaiting = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    if (open && !wasOpen.current) {
      const active = document.activeElement as HTMLElement | null;
      opener.current = active && active !== document.body ? active : null;
      if (!opener.current) {
        // What opened the record left the DOM with it (a palette row), so
        // focus is nowhere yet. The overlay that held it returns focus to
        // its own opener a moment later; that element is the one to come
        // back to. Focus moving into the panel itself does not count.
        const onFocusIn = (event: FocusEvent) => {
          const target = event.target as HTMLElement | null;
          if (!target || target.closest('section[aria-label="Record"], section[aria-label="Form"]'))
            return;
          opener.current = target;
          stop();
        };
        const timer = window.setTimeout(() => stop(), 500);
        const stop = () => {
          document.removeEventListener('focusin', onFocusIn);
          window.clearTimeout(timer);
          stopWaiting.current = null;
        };
        document.addEventListener('focusin', onFocusIn);
        stopWaiting.current = stop;
      }
    }
    if (!open && wasOpen.current) {
      stopWaiting.current?.();
      const target = opener.current;
      opener.current = null;
      if (target && document.contains(target)) target.focus();
    }
    wasOpen.current = open;
  }, [open]);

  useEffect(() => () => stopWaiting.current?.(), []);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // An open dialog, menu, or palette owns Escape first; Radix marks the
      // event handled before it reaches the document.
      close();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);
}

export function RecordPanel() {
  const panel = useRecordPanel();
  const { current, form, depth, closeForm } = panel;
  const confirm = useConfirm();
  const dirty = useRef(false);
  const requestClose = useCallback(() => {
    if (!dirty.current) {
      closeForm();
      return;
    }
    void confirm(DISCARD_FORM).then((ok) => {
      if (ok) closeForm();
    });
  }, [closeForm, confirm]);
  const guard = useMemo(() => ({ dirty, requestClose }), [requestClose]);
  useRecordPanelHost(current !== null || form !== null, form ? requestClose : panel.close);

  if (form) {
    return (
      <section
        aria-label="Form"
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <PanelLayout
          header={
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
              <div style={{ flex: '1 1 0%', minWidth: 0 }}>
                <PanelHeader title={formTitle(form.name)} icon="edit_square" />
              </div>
              <div style={{ padding: '9px 11px 0 0' }}>
                <IconButton
                  icon="close"
                  size={30}
                  tone="bare"
                  aria-label="Close form"
                  onClick={requestClose}
                />
              </div>
            </div>
          }
        >
          <PanelFormGuardContext.Provider value={guard}>
            <FormBody key={`${form.orgId ?? ''}:${form.name}:${form.id ?? ''}`} form={form} />
          </PanelFormGuardContext.Provider>
        </PanelLayout>
      </section>
    );
  }

  if (!current) return null;

  return (
    <section
      aria-label="Record"
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <PanelLayout
        header={
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {depth > 0 ? (
              <div style={{ padding: '7px 11px 0' }}>
                <Button variant="ghost" size="sm" icon="arrow_back" onClick={panel.back}>
                  Back
                </Button>
              </div>
            ) : null}
            <PanelHeader kindLabel={kindLabel(current.kind).toUpperCase()} onClose={panel.close} />
          </div>
        }
      >
        <RecordBody key={`${current.kind}:${current.orgId ?? ''}:${current.id}`} record={current} />
      </PanelLayout>
    </section>
  );
}
